import React, { useState, useEffect, useRef } from 'react';
import { getSettings, subscribeToSettings } from '../firebase/firebase-config';

const HeaderBanner = ({ 
  images = [], 
  videoUrl = '', 
  useVideo = false,
  autoPlay = true,
  muted = true,
  height = '500px'
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(muted);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);

  const validImages = images?.filter(img => img && img.url) || [];
  const hasImages = validImages.length > 0;

  useEffect(() => {
    if (!useVideo && hasImages && validImages.length > 1 && autoPlay) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % validImages.length);
      }, 5000);
      return () => clearInterval(intervalRef.current);
    }
  }, [useVideo, validImages, autoPlay]);

  useEffect(() => {
    if (useVideo && videoRef.current) {
      if (isPlaying) videoRef.current.play();
      else videoRef.current.pause();
    }
  }, [isPlaying, useVideo]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      if (autoPlay && validImages.length > 1) {
        intervalRef.current = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % validImages.length);
        }, 5000);
      }
    }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % validImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + validImages.length) % validImages.length);
  const togglePlayPause = () => setIsPlaying(!isPlaying);
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      nextSlide();
    }
    if (touchStart - touchEnd < -50) {
      prevSlide();
    }
  };

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?#]+)/);
    return match ? match[1] : null;
  };

  const isYouTubeUrl = (url) => url?.includes('youtube.com') || url?.includes('youtu.be');

  if ((!useVideo && !hasImages) || (useVideo && !videoUrl)) {
    return null;
  }

  const currentImage = validImages[currentSlide];

  return (
    <div className="header-banner" style={{ height }}>
      {!useVideo && hasImages && currentImage && (
        <>
          <div className="slider-container">
            <div className="slider-slide active">
              <div className="blur-bg" style={{ backgroundImage: `url(${currentImage.url})` }} />
              <div className="image-wrapper">
                <img 
                  src={currentImage.url} 
                  alt={`Slide ${currentSlide + 1}`}
                  className="banner-image"
                  style={{
                    objectPosition: `${currentImage.positionX || 50}% ${currentImage.positionY || 50}%`,
                    transform: `scale(${(currentImage.zoom || 100) / 100})`
                  }}
                />
              </div>
            </div>
          </div>
          
          {validImages.length > 1 && (
            <>
              <button className="slider-nav prev" onClick={prevSlide}>❮</button>
              <button className="slider-nav next" onClick={nextSlide}>❯</button>
              <div className="slider-dots">
                {validImages.map((_, index) => (
                  <span 
                    key={index} 
                    className={`dot ${index === currentSlide ? 'active' : ''}`} 
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
      
      {useVideo && videoUrl && (
        <div className="video-container">
          {isYouTubeUrl(videoUrl) ? (
            <iframe 
              src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}?autoplay=${autoPlay ? 1 : 0}&mute=${muted ? 1 : 0}&loop=1&playlist=${getYouTubeId(videoUrl)}&controls=1`} 
              title="Banner Video" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen 
            />
          ) : (
            <>
              <video ref={videoRef} src={videoUrl} autoPlay={autoPlay} muted={isMuted} loop playsInline className="banner-video" />
              <div className="video-controls">
                <button className="video-control-btn" onClick={togglePlayPause}>{isPlaying ? '⏸' : '▶'}</button>
                <button className="video-control-btn" onClick={toggleMute}>{isMuted ? '🔇' : '🔊'}</button>
              </div>
            </>
          )}
        </div>
      )}
      
      <style>{`
        .header-banner {
          position: relative;
          overflow: hidden;
          width: 100%;
          background: #000;
        }
        
        .slider-container {
          position: relative;
          width: 100%;
          height: 100%;
        }
        
        .slider-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 1;
          z-index: 1;
        }
        
        .blur-bg {
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          background-size: cover;
          background-position: center;
          filter: blur(20px);
          opacity: 0.5;
          z-index: 1;
        }
        
        .image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          background: #000;
        }
        
        .banner-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: all 0.3s ease;
        }
        
        /* Desktop */
        @media (min-width: 1024px) {
          .header-banner {
            height: 500px !important;
            max-height: 70vh;
          }
        }
        
        /* Tablet */
        @media (min-width: 768px) and (max-width: 1023px) {
          .header-banner {
            height: 450px !important;
          }
        }
        
        /* Mobile */
        @media (max-width: 767px) {
          .header-banner {
            height: 500px !important;
          }
        }
        
        .slider-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(4px);
          color: white;
          border: none;
          width: 45px;
          height: 45px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
          z-index: 20;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .slider-nav:hover {
          background: rgba(0,0,0,0.8);
          transform: translateY(-50%) scale(1.1);
        }
        
        .slider-nav.prev {
          left: 20px;
        }
        
        .slider-nav.next {
          right: 20px;
        }
        
        .slider-dots {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 20;
        }
        
        .slider-dots .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255,255,255,0.5);
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .slider-dots .dot:hover {
          background: rgba(255,255,255,0.8);
          transform: scale(1.2);
        }
        
        .slider-dots .dot.active {
          background: #f59e0b;
          transform: scale(1.2);
        }
        
        .video-container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: #000;
        }
        
        .banner-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .video-container iframe {
          width: 100%;
          height: 100%;
          border: none;
        }
        
        .video-controls {
          position: absolute;
          bottom: 20px;
          right: 20px;
          display: flex;
          gap: 10px;
          z-index: 20;
        }
        
        .video-control-btn {
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(4px);
          color: white;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .video-control-btn:hover {
          background: rgba(0,0,0,0.8);
          transform: scale(1.1);
        }
        
        @media (max-width: 768px) {
          .slider-nav {
            width: 35px;
            height: 35px;
            font-size: 16px;
          }
          .slider-nav.prev {
            left: 10px;
          }
          .slider-nav.next {
            right: 10px;
          }
          .slider-dots {
            bottom: 15px;
            gap: 8px;
          }
          .slider-dots .dot {
            width: 8px;
            height: 8px;
          }
        }
      `}</style>
    </div>
  );
};

// Global banner component that fetches from Firebase Settings
export const GlobalHeaderBanner = () => {
  const [bannerData, setBannerData] = useState({
    images: [],
    globalBannerVideo: '',
    useVideoBanner: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBanner = async () => {
      try {
        const settingsData = await getSettings();
        setBannerData({
          images: settingsData.globalBannerImages || [],
          globalBannerVideo: settingsData.globalBannerVideo || '',
          useVideoBanner: settingsData.useVideoBanner || false
        });
      } catch (error) {
        console.error('Error loading banner:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBanner();

    const unsubscribe = subscribeToSettings((data) => {
      if (data) {
        setBannerData({
          images: data.globalBannerImages || [],
          globalBannerVideo: data.globalBannerVideo || '',
          useVideoBanner: data.useVideoBanner || false
        });
      }
    });
    
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{ height: '500px', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner"></div>
        <style>{`
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <HeaderBanner 
      images={bannerData.images}
      videoUrl={bannerData.globalBannerVideo}
      useVideo={bannerData.useVideoBanner}
      autoPlay={true}
      muted={true}
      height="500px"
    />
  );
};

export default HeaderBanner;